/**
 * Signaling over BroadcastChannel: two tabs in one browser, no server at all.
 *
 * This is what step 4 connects with, and what the Playwright specs use as the
 * local signaling stub. The PHP client implements the same interface.
 */
import {
  type SignalHandler,
  type SignalMessage,
  type Signaling,
  SignalHandlers,
  addressedTo,
  makePeerId,
} from './signaling.js';

export class BroadcastSignaling implements Signaling {
  readonly room: string;
  readonly peerId: string;
  private channel: BroadcastChannel | null = null;
  private readonly handlers = new SignalHandlers();

  constructor(room: string, peerId: string = makePeerId()) {
    this.room = room;
    this.peerId = peerId;
  }

  start(): Promise<void> {
    if (this.channel) return Promise.resolve();
    const channel = new BroadcastChannel(`office-games:${this.room}`);
    channel.onmessage = (event: MessageEvent): void => {
      const message = event.data as SignalMessage;
      if (!message || typeof message !== 'object') return;
      if (!addressedTo(message, this.peerId)) return;
      this.handlers.emit(message);
    };
    this.channel = channel;
    return Promise.resolve();
  }

  send(message: Omit<SignalMessage, 'from'>): Promise<void> {
    this.channel?.postMessage({ ...message, from: this.peerId } satisfies SignalMessage);
    return Promise.resolve();
  }

  onMessage(handler: SignalHandler): () => void {
    return this.handlers.add(handler);
  }

  close(): void {
    void this.send({ to: null, kind: 'leave' });
    this.channel?.close();
    this.channel = null;
    this.handlers.clear();
  }
}
